<template>
  <div class="gam-chessboard-wrapper">
    <div
      ref="boardRef"
      :class="['gam-chessboard', { 'is-flipped': isFlipped }]"
      @touchstart.prevent="onPointerDown"
      @mousedown.prevent="onPointerDown"
    >
      <template v-for="(sq, idx) in squares" :key="sq.key">
        <div
          :class="[
            'square',
            sq.color,
            {
              'is-selected': sq.square === selectedSquare,
              'is-legal-target': isLegalTarget(sq.square),
              'is-last-move': isLastMoveSquare(sq.square),
              'is-check': isCheckSquare(sq.square),
            },
          ]"
          :data-square="sq.square"
          :data-index="idx"
        >
          <span v-if="showFileLabel(sq)" :class="['coord-label', 'file-label', { 'is-flipped': isFlipped }]">
            {{ sq.file }}
          </span>
          <span v-if="showRankLabel(sq)" :class="['coord-label', 'rank-label', { 'is-flipped': isFlipped }]">
            {{ sq.rank }}
          </span>

          <div v-if="isLegalTarget(sq.square)" class="legal-dot-container">
            <div :class="sq.piece ? 'legal-ring' : 'legal-dot'" />
          </div>

          <GamChessboardPiece
            v-if="sq.piece && !(isDragging && sq.square === dragSquare) && sq.square !== animatingFromSquare"
            :piece="sq.piece"
            :is-flipped="isFlipped"
          />
        </div>
      </template>
    </div>

    <!--
      The picker sits outside the board: the grid clips its overflow and is
      rotated for black, both of which would hide or invert the options.
    -->
    <div
      v-if="promotionSquare"
      class="promotion-layer"
      @mousedown.self.prevent="cancelPromotion"
      @touchstart.self.prevent="cancelPromotion"
    >
      <GamChessboardPromotion
        class="promotion-picker"
        :style="promotionStyle"
        :color="engine.turn.value"
        @select="onPromotionSelect"
      />
    </div>

    <!-- Dragging ghost piece -->
    <div
      v-if="dragPiece && isDragging"
      class="drag-ghost"
      :style="{ left: `${dragX}px`, top: `${dragY}px`, width: `${squareSize}px`, height: `${squareSize}px` }"
    >
      <GamChessboardPiece :piece="dragPiece" />
    </div>

    <!-- Move animation piece -->
    <div
      v-if="animatingPiece"
      class="anim-piece"
      :style="{
        left: `${animFromX}px`,
        top: `${animFromY}px`,
        width: `${squareSize}px`,
        height: `${squareSize}px`,
        transform: `translate(${animToX - animFromX}px, ${animToY - animFromY}px)`,
        transition: `transform ${animDurationMs}ms var(--cubic-bezier, ease)`,
      }"
    >
      <GamChessboardPiece :piece="animatingPiece" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Square } from 'chess.js';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import {
  GamChessboardMode,
  GamChessboardOrientation,
} from '@/views/composables/constants/components/gamChessboard.constants';
import { useChessEngine } from '@/views/composables/hooks/useChessEngine';
import type { ChessboardMoveEvent, GamChessboardType } from '@/views/composables/models/components/GamChessboard';

import GamChessboardPiece from './GamChessboardPiece.vue';
import GamChessboardPromotion from './GamChessboardPromotion.vue';

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const RANKS = ['8', '7', '6', '5', '4', '3', '2', '1'];

const props = withDefaults(defineProps<GamChessboardType>(), {
  mode: GamChessboardMode.PLAY,
  orientation: GamChessboardOrientation.AUTO,
  isInteractive: true,
  isAnimated: true,
});

const emit = defineEmits<{
  move: [event: ChessboardMoveEvent];
  'blocked-touch': [];
}>();

const engine = useChessEngine(props.fen);

const boardRef = ref<HTMLDivElement>();
const selectedSquare = ref<Square | null>(null);
const legalTargets = ref<Square[]>([]);
const promotionSquare = ref<Square | null>(null);
const pendingPromotion = ref<{ from: Square; to: Square } | null>(null);

const isDragging = ref(false);
const dragSquare = ref<Square | null>(null);
const dragPiece = ref<string | null>(null);
const dragX = ref(0);
const dragY = ref(0);
const squareSize = ref(0);

const animatingPiece = ref<string | null>(null);
const animatingFromSquare = ref<string | null>(null);
const animFromX = ref(0);
const animFromY = ref(0);
const animToX = ref(0);
const animToY = ref(0);
const animDurationMs = ref(200);

const lastMoveFrom = ref<string | null>(props.lastMoveFrom || null);
const lastMoveTo = ref<string | null>(props.lastMoveTo || null);

const FLIP_DELAY_MS = 800;
let flipTimer: ReturnType<typeof setTimeout> | null = null;

const isFlipped = ref(
  props.orientation === GamChessboardOrientation.BLACK ||
    (props.orientation === GamChessboardOrientation.AUTO && engine.turn.value === 'b'),
);

function resolveFlipped(): boolean {
  if (props.orientation === GamChessboardOrientation.WHITE) return false;
  if (props.orientation === GamChessboardOrientation.BLACK) return true;
  return engine.turn.value === 'b';
}

function scheduleFlip(): void {
  if (flipTimer) clearTimeout(flipTimer);
  flipTimer = setTimeout(() => {
    isFlipped.value = resolveFlipped();
    flipTimer = null;
  }, FLIP_DELAY_MS);
}

interface SquareData {
  key: string;
  square: Square;
  file: string;
  rank: string;
  color: 'light' | 'dark';
  piece: string | null;
  row: number;
  col: number;
}

const squares = computed((): SquareData[] => {
  const result: SquareData[] = [];
  const boardData = engine.board.value;

  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const file = FILES[c];
      const rank = RANKS[r];
      const square = `${file}${rank}` as Square;
      const cell = boardData[r][c];
      let piece: string | null = null;

      if (cell) {
        piece = cell.color === 'w' ? cell.type.toUpperCase() : cell.type.toLowerCase();
      }

      result.push({
        key: square,
        square,
        file,
        rank,
        color: (r + c) % 2 === 0 ? 'light' : 'dark',
        piece,
        row: r,
        col: c,
      });
    }
  }
  return result;
});

// The board is rotated for black, so the labelled edges swap to keep the files
// along the bottom and the ranks down the left side for both colours.
function showFileLabel(sq: SquareData): boolean {
  return isFlipped.value ? sq.row === 0 : sq.row === 7;
}

function showRankLabel(sq: SquareData): boolean {
  return isFlipped.value ? sq.col === 7 : sq.col === 0;
}

function isLegalTarget(square: Square): boolean {
  return legalTargets.value.includes(square);
}

function isLastMoveSquare(square: Square): boolean {
  return square === lastMoveFrom.value || square === lastMoveTo.value;
}

const checkSquare = computed((): Square | null => {
  if (!engine.isCheck.value) return null;
  const boardData = engine.board.value;
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const cell = boardData[r][c];
      if (cell && cell.type === 'k' && cell.color === engine.turn.value) {
        return `${FILES[c]}${RANKS[r]}` as Square;
      }
    }
  }
  return null;
});

function isCheckSquare(square: Square): boolean {
  return checkSquare.value === square;
}

function clearSelection(): void {
  selectedSquare.value = null;
  legalTargets.value = [];
}

function selectSquare(square: Square): void {
  const moves = engine.getLegalMoves(square);
  if (moves.length === 0) {
    clearSelection();
    return;
  }
  selectedSquare.value = square;
  legalTargets.value = moves.map((m) => m.to as Square);
}

function tryMove(from: Square, to: Square, promotion?: string): void {
  if (!props.isInteractive || props.mode !== GamChessboardMode.PLAY) return;

  if (!promotion && engine.needsPromotion(from, to)) {
    if (props.skipPromotion) {
      promotion = 'q';
    } else {
      promotionSquare.value = to;
      pendingPromotion.value = { from, to };
      return;
    }
  }

  const move = engine.makeMove(from, to, promotion);
  if (move) {
    lastMoveFrom.value = move.from;
    lastMoveTo.value = move.to;
    emit('move', {
      from: move.from,
      to: move.to,
      promotion: move.promotion,
      san: move.san,
      fen: engine.fen.value,
    });
    scheduleFlip();
  }

  clearSelection();
}

function onPromotionSelect(piece: string): void {
  const pending = pendingPromotion.value;
  promotionSquare.value = null;
  pendingPromotion.value = null;
  if (pending) tryMove(pending.from, pending.to, piece);
}

function cancelPromotion(): void {
  promotionSquare.value = null;
  pendingPromotion.value = null;
  clearSelection();
}

/**
 * Places the four options over the promotion file, growing away from the edge
 * the pawn arrived at so they always stay on the board.
 */
const promotionStyle = computed((): Record<string, string> => {
  const square = promotionSquare.value;
  if (!square) return {};

  const fileIndex = FILES.indexOf(square[0]);
  const rankIndex = RANKS.indexOf(square[1]);
  const column = isFlipped.value ? 7 - fileIndex : fileIndex;
  const row = isFlipped.value ? 7 - rankIndex : rankIndex;
  const left = `${column * 12.5}%`;

  return row <= 3 ? { left, top: `${row * 12.5}%` } : { left, bottom: `${(7 - row) * 12.5}%` };
});

function getSquareFromPoint(clientX: number, clientY: number): Square | null {
  const el = boardRef.value;
  if (!el) return null;

  const rect = el.getBoundingClientRect();
  const size = rect.width / 8;
  let col = Math.floor((clientX - rect.left) / size);
  let row = Math.floor((clientY - rect.top) / size);

  if (isFlipped.value) {
    col = 7 - col;
    row = 7 - row;
  }

  if (col < 0 || col > 7 || row < 0 || row > 7) return null;
  return `${FILES[col]}${RANKS[row]}` as Square;
}

function getPieceAt(square: Square): string | null {
  const s = squares.value.find((sq) => sq.square === square);
  return s?.piece || null;
}

function isOwnPiece(square: Square): boolean {
  const piece = getPieceAt(square);
  if (!piece) return false;
  const isWhite = piece === piece.toUpperCase();
  return (engine.turn.value === 'w' && isWhite) || (engine.turn.value === 'b' && !isWhite);
}

function updateSquareSize(): void {
  const el = boardRef.value;
  if (el) {
    squareSize.value = el.getBoundingClientRect().width / 8;
  }
}

function getSquarePixelCenter(square: Square): { x: number; y: number } | null {
  const el = boardRef.value;
  if (!el) return null;

  const rect = el.getBoundingClientRect();
  const fileIdx = FILES.indexOf(square[0]);
  const rankIdx = RANKS.indexOf(square[1]);
  const size = rect.width / 8;

  let col = fileIdx;
  let row = rankIdx;
  if (isFlipped.value) {
    col = 7 - col;
    row = 7 - row;
  }

  return {
    x: rect.left + col * size + size / 2 - size / 2,
    y: rect.top + row * size + size / 2 - size / 2,
  };
}

async function animateMove(from: Square, to: Square, piece: string, duration = 200): Promise<void> {
  if (!props.isAnimated) return;

  const fromPos = getSquarePixelCenter(from);
  const toPos = getSquarePixelCenter(to);
  if (!fromPos || !toPos) return;

  animDurationMs.value = duration;
  animFromX.value = fromPos.x;
  animFromY.value = fromPos.y;
  animToX.value = fromPos.x;
  animToY.value = fromPos.y;
  animatingPiece.value = piece;
  animatingFromSquare.value = from;

  await nextTick();

  animToX.value = toPos.x;
  animToY.value = toPos.y;

  await new Promise((resolve) => setTimeout(resolve, duration + 20));
  animatingPiece.value = null;
  animatingFromSquare.value = null;
}

function onPointerDown(e: MouseEvent | TouchEvent): void {
  if (!props.isInteractive || props.mode !== GamChessboardMode.PLAY) {
    emit('blocked-touch');
    return;
  }
  if (promotionSquare.value) return;

  const point = 'touches' in e ? e.touches[0] : e;
  const square = getSquareFromPoint(point.clientX, point.clientY);
  if (!square) return;

  updateSquareSize();

  if (selectedSquare.value && isLegalTarget(square)) {
    tryMove(selectedSquare.value, square);
    return;
  }

  if (selectedSquare.value && square === selectedSquare.value) {
    clearSelection();
    return;
  }

  if (isOwnPiece(square)) {
    selectSquare(square);

    dragSquare.value = square;
    dragPiece.value = getPieceAt(square);
    dragX.value = point.clientX - squareSize.value / 2;
    dragY.value = point.clientY - squareSize.value / 2;

    const onMove = (ev: MouseEvent | TouchEvent) => {
      isDragging.value = true;
      const p = 'touches' in ev ? ev.touches[0] : ev;
      dragX.value = p.clientX - squareSize.value / 2;
      dragY.value = p.clientY - squareSize.value / 2;
    };

    const onUp = (ev: MouseEvent | TouchEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);

      if (isDragging.value) {
        const p = 'changedTouches' in ev ? ev.changedTouches[0] : ev;
        const target = getSquareFromPoint(p.clientX, p.clientY);
        if (target && dragSquare.value && target !== dragSquare.value && isLegalTarget(target)) {
          tryMove(dragSquare.value, target);
        }
      }

      isDragging.value = false;
      dragSquare.value = null;
      dragPiece.value = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return;
  }

  clearSelection();
}

watch(
  () => props.fen,
  (newFen) => {
    if (newFen && newFen !== engine.fen.value) {
      engine.loadFen(newFen);
      scheduleFlip();
    }
  },
);

watch(
  () => props.lastMoveFrom,
  (v) => {
    lastMoveFrom.value = v || null;
  },
);

watch(
  () => props.lastMoveTo,
  (v) => {
    lastMoveTo.value = v || null;
  },
);

onMounted(() => {
  updateSquareSize();
  window.addEventListener('resize', updateSquareSize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateSquareSize);
  if (flipTimer) clearTimeout(flipTimer);
});

defineExpose({
  engine,
  animateMove,
  lastMoveFrom,
  lastMoveTo,
});
</script>

<style lang="scss" scoped>
.gam-chessboard-wrapper {
  position: relative;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
}

.gam-chessboard {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  grid-template-rows: repeat(8, 1fr);
  aspect-ratio: 1;
  width: 100%;
  border-radius: var(--radius-x-small);
  overflow: hidden;
  transition: transform 1.5s var(--cubic-bezier);
  box-shadow: var(--shadow-card-base);

  &.is-flipped {
    transform: rotate(180deg);
  }
}

.square {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  // No per-cell aspect-ratio: the flat 8x8 grid already makes every cell square
  // from 1fr x 1fr, and Safari does not recalculate aspect-ratio on grid items
  // during a live viewport resize, which leaves cells at stale dimensions.
  cursor: pointer;

  &.light {
    background: #f0d9b5;
  }

  &.dark {
    background: #b58863;
  }

  &.is-selected {
    box-shadow: inset 0 0 0 100px rgba(0, 150, 100, 0.4);
  }

  &.is-last-move {
    box-shadow: inset 0 0 0 100px rgba(155, 199, 0, 0.35);
  }

  &.is-check {
    box-shadow: inset 0 0 0 100px rgba(255, 0, 0, 0.4);
  }
}

.promotion-layer {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: rgba(0, 0, 0, 0.45);
  border-radius: var(--radius-x-small);
}

.promotion-picker {
  position: absolute;
  width: 12.5%;
}

.legal-dot-container {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
}

.legal-dot {
  width: 28%;
  height: 28%;
  border-radius: 50%;
  background: var(--color-white-25);
}

.legal-ring {
  width: 85%;
  height: 85%;
  border-radius: 50%;
  border: 3px solid var(--color-white-25);
  box-sizing: border-box;
}

.coord-label {
  position: absolute;
  font-size: 10px;
  font-weight: 700;
  pointer-events: none;
  user-select: none;
  line-height: 1;
  z-index: 1;

  .light > & {
    color: #b58863;
  }

  .dark > & {
    color: #f0d9b5;
  }

  &.is-flipped {
    transform: rotate(180deg);
  }
}

.file-label {
  bottom: 2px;
  right: 3px;

  // The square itself is upside down, so the corners are mirrored too.
  &.is-flipped {
    top: 2px;
    left: 3px;
    bottom: auto;
    right: auto;
  }
}

.rank-label {
  top: 2px;
  left: 3px;

  &.is-flipped {
    bottom: 2px;
    right: 3px;
    top: auto;
    left: auto;
  }
}

.drag-ghost {
  position: fixed;
  pointer-events: none;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(1.15);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5));
}

.anim-piece {
  position: fixed;
  pointer-events: none;
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
